import { FieldOption, LogicElement, LogicGroup, LogicOperator } from '@/types/logic';
import { humanize } from '@/utils/strings';

export interface CriteriaClassification {
  type: 'classification';
  label?: string;
  value: string;
}

export interface CriteriaGroup {
  type: 'group';
  operator: LogicOperator;
  parts: CriteriaContent[];
}

export type CriteriaContent = CriteriaClassification | CriteriaGroup;

// Classification keys are always "<category>.<slug>" (e.g. "jurisdiction.arizona" --
// see ClassificationType's key/name generation in BaseRule._get_type_name), so a
// choice's own value -- not its display label -- is what tells us its category.
const splitChoiceValue = (
  rawValue: string,
  fallbackLabel: string
): { label?: string; value: string } => {
  const dotIndex = rawValue.indexOf('.');
  if (dotIndex === -1) return { value: fallbackLabel };
  return {
    label: humanize(rawValue.slice(0, dotIndex)),
    value: humanize(rawValue.slice(dotIndex + 1))
  };
};

const classificationsForElement = (
  element: LogicElement,
  fieldOptionsByField: Record<string, FieldOption>
): CriteriaClassification[] => {
  const option = fieldOptionsByField[element.field];
  const negate = (value: string) => (element.is_negated ? `Not ${value}` : value);

  if (option?.value_choices) {
    const rawValues = Array.isArray(element.value) ? element.value : [element.value];
    return rawValues.map((rawValue) => {
      const choice = option.value_choices?.find((candidate) => candidate.value === rawValue);
      const { label, value } = splitChoiceValue(
        String(rawValue),
        choice?.label ?? String(rawValue)
      );
      return { type: 'classification', label, value: negate(value) };
    });
  }

  return [{ type: 'classification', value: negate(option?.label ?? element.field) }];
};

const buildGroupContent = (
  group: LogicGroup,
  fieldOptionsByField: Record<string, FieldOption>
): CriteriaContent | null => {
  const parts: CriteriaContent[] = [
    ...group.logic_elements.flatMap((element) =>
      classificationsForElement(element, fieldOptionsByField)
    ),
    ...group.logic_groups
      .map((child) => buildGroupContent(child, fieldOptionsByField))
      .filter((content): content is CriteriaContent => content !== null)
  ];

  if (parts.length === 0) return null;
  return { type: 'group', operator: group.logic_operator, parts };
};

/**
 * Builds a renderable tree from a treatment group's eligibility root
 * LogicGroups. Every real LogicGroup always renders with its own operator
 * label, however many parts it has; only the synthetic top-level wrapper
 * combining independent root groups is collapsed when there's just one.
 */
export const buildEligibilityCriteriaTree = (
  rootGroups: LogicGroup[],
  fieldOptions: FieldOption[]
): CriteriaContent | null => {
  const fieldOptionsByField = Object.fromEntries(
    fieldOptions.map((option) => [option.field, option])
  );

  const contents = rootGroups
    .map((group) => buildGroupContent(group, fieldOptionsByField))
    .filter((content): content is CriteriaContent => content !== null);

  if (contents.length === 0) return null;
  if (contents.length === 1) return contents[0];
  return { type: 'group', operator: LogicOperator.AND, parts: contents };
};
